// ContactWorkflowCard — the "Workflow Status" card embedded on the contact
// profile (audit / Contact Journey spec §1). Compact, read-only view of what
// automation a contact is in, in plain language. Reuses /api/contacts/
// workflow-status. Light-themed to match the client profile modal.

import React from 'react';
import { bearer } from '../lib/authedFetch';

// status → chip classes (light theme).
function chip(e) {
  if (e.status === 'failed') return { cls: 'bg-rose-50 text-rose-700 border-rose-200', label: 'Failed' };
  if (e.status === 'active' && e.status_label === 'Waiting') return { cls: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Waiting' };
  if (e.status === 'active') return { cls: 'bg-sky-50 text-sky-700 border-sky-200', label: 'Running' };
  if (e.status === 'paused') return { cls: 'bg-slate-100 text-slate-600 border-slate-200', label: 'Paused' };
  return { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: e.status_label || 'Done' };
}

export default function ContactWorkflowCard({ clientId, targetType = 'client' }) {
  const [state, setState] = React.useState({ loading: true, rows: [], error: null });

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!clientId) { setState({ loading: false, rows: [], error: null }); return; }
      try {
        const res = await fetch(`/api/contacts/workflow-status?target_type=${encodeURIComponent(targetType)}&target_id=${encodeURIComponent(clientId)}`, { headers: { ...(await bearer()) } });
        const j = await res.json().catch(() => null);
        if (cancelled) return;
        if (!res.ok) { setState({ loading: false, rows: [], error: j?.error || `${res.status}` }); return; }
        setState({ loading: false, rows: j.enrollments || [], error: null });
      } catch (e) { if (!cancelled) setState({ loading: false, rows: [], error: e.message }); }
    })();
    return () => { cancelled = true; };
  }, [clientId, targetType]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-slate-900">Workflow Status</h3>
        {state.rows.length > 0 && <span className="text-[11px] text-slate-400">{state.rows.filter((e) => e.status === 'active').length} active</span>}
      </div>

      {state.loading ? <div className="text-xs text-slate-400">Loading…</div>
        : state.error ? <div className="text-xs text-slate-400">Workflow status unavailable.</div>
        : state.rows.length === 0 ? <div className="text-xs text-slate-500">Not currently in any workflow.</div>
        : state.rows.slice(0, 4).map((e) => {
          const c = chip(e);
          return (
            <div key={e.enrollment_id} className="border-t border-slate-100 pt-3 mt-3 first:border-t-0 first:pt-0 first:mt-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] font-semibold text-slate-800">{e.workflow}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${c.cls}`}>{c.label}</span>
              </div>
              <div className="text-[11.5px] text-slate-500 mt-1">{e.why_waiting}</div>
              {e.next_action_label && <div className="text-[11.5px] text-amber-700 mt-0.5">→ {e.next_action_label}</div>}

              {(e.flow || []).length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1">
                  {e.flow.map((n, i) => {
                    const cur = n.position === 'current';
                    const done = n.position === 'done';
                    return (
                      <span key={n.id} className="inline-flex items-center gap-1">
                        <span className={`text-[10.5px] px-1.5 py-0.5 rounded ${cur ? 'bg-amber-100 text-amber-800 font-bold' : done ? 'text-emerald-600' : 'text-slate-400'}`}>
                          {done ? '✓ ' : cur ? '◉ ' : ''}{n.action}
                        </span>
                        {i < e.flow.length - 1 && <span className="text-slate-300 text-[10px]">→</span>}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
}
